// Defense-in-depth gate for an EXPIRED subscription. Even if the UI is
// tampered with (DOM/CSS hacks, React DevTools state edits, etc.), the
// outbound API client consults this flag and refuses any state-mutating
// request, so the user cannot create / update / delete server-side records
// after their trial / subscription has ended.

let subscriptionExpired = false;

export const setSubscriptionExpired = (value: boolean): void => {
  subscriptionExpired = value;
};

export const isSubscriptionExpired = (): boolean => subscriptionExpired;

// Read-only HTTP methods are still permitted while the gate is closed so
// that the SubscriptionExpired UI can still load the bare minimum it needs
// (e.g. the current user).
const READ_ONLY_METHODS = new Set(['get', 'head', 'options']);

// Endpoints (path prefixes) that must keep working even after expiration.
// EXTEND THIS LIST whenever you add a flow that the user must be able to
// complete from the SubscriptionExpired modal — e.g. paying an invoice,
// updating a card, or otherwise reactivating the subscription.
// Without this, the request will be rejected client-side with
// SubscriptionExpiredError and the UI will appear silently broken.
const ALWAYS_ALLOWED_PATH_PREFIXES = [
  '/api/light/billing/', // reactivation / payment-method endpoints
  '/api/light/auth/logout/', // must work even when subscription is expired
];

const stripBaseUrl = (url: string): string => {
  // Accept absolute URLs (e.g. when callers pass a full URL) by reducing
  // them to the pathname. Falls back to the raw string for anything that
  // isn't a parsable URL.
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

export const isRequestBlockedBySubscription = (
  method: string | undefined,
  url: string | undefined
): boolean => {
  if (!subscriptionExpired) return false;

  const normalizedMethod = (method || 'get').toLowerCase();
  if (READ_ONLY_METHODS.has(normalizedMethod)) return false;

  const path = stripBaseUrl(url || '');
  if (ALWAYS_ALLOWED_PATH_PREFIXES.some((p) => path.startsWith(p))) {
    return false;
  }

  return true;
};

export class SubscriptionExpiredError extends Error {
  constructor() {
    super(
      'Your subscription has expired. This action is not allowed until your subscription is renewed.'
    );
    this.name = 'SubscriptionExpiredError';
  }
}
